// Rhythm: 16-step drum grids, fills, EP comping cells, swing, and the per-bar drum hit planner.

const GRIDS = {
  P1: { name: 'boom',   K: 'x.........x.....', S: '....x.......x...', H: 'x.x.x.x.x.x.x.x.' },
  P2: { name: 'bounce', K: 'x......x..x.....', S: '....x.......x...', H: 'x.x.x.x.x.x.x.xx' },
  P3: { name: 'lazy',   K: 'x.....x.........', S: '....x..g....x...', H: 'x.x.x.x.x.x.x.o.' },
  P4: { name: 'half',   K: 'x.....x...x.....', S: '........x.......', H: 'x.x.x.x.x.x.x.x.', R: '............r...' },
  P5: { name: 'rim',    K: 'x.........x.....', R: '....r.......r...', H: 'x...x...x...x...' },
  P6: { name: 'shuf',   K: 'x..x......x.....', S: '....x..g....x.g.', H: 'xxxxxxxxxxxxxxxx' },
};
const lane = s => [...(s || '')].map((c, i) => [c, i]).filter(([c]) => c !== '.');

/** Drum lanes, one letter per voice: K S H R are the kit (createKit); C clap, X shaker, T tom and W block are the
 *  percussion the styles add (createPerc). */
export const LANES = { K: 'kick', S: 'snare', H: 'hat', R: 'rim', C: 'clap', X: 'shaker', T: 'tom', W: 'block' };
export const DRUM_VOICES = Object.values(LANES);
const ACCENT = { a: 1.2, s: 0.55 };
/** One grid { name?, K: '16 steps', S, … } → { name, hits: [{ v, s, ghost?, open?, acc? }] }.
 *  Steps: x hit · g ghost · o open (hat) · a accent (×1.2) · s soft (×0.55); any other letter is a plain hit. */
export function parseGrid(g) {
  const hits = [];
  for (const [L, v] of Object.entries(LANES)) for (const [c, s] of lane(g[L])) {
    const h = { v, s };
    if (v === 'snare') h.ghost = c === 'g';
    else if (v === 'hat') h.open = c === 'o';
    else if (c === 'g') h.ghost = true;
    if (ACCENT[c]) h.acc = ACCENT[c];
    hits.push(h);
  }
  return { name: g.name, hits };
}

/** Parsed grids: { name, hits: [{ v: 'kick'|'snare'|'hat'|'rim', s, ghost?, open? }] } */
export const DRUMS = Object.fromEntries(Object.entries(GRIDS).map(([id, g]) => [id, parseGrid(g)]));

/** Fills for the last bar of a phrase: F1 snare roll, F2 stop, F3 kick pickups, F4 open hat.
 *  A style's fills may add `voice` (the roll's or the pickups' voice, default snare / kick). */
export const FILLS = {
  F1: { roll: [[12, 0.9], [13, 0.3], [14, 0.45], [15, 0.65]] },
  F2: { stop: 12 },
  F3: { kicks: [13, 15] },
  F4: { open: 14 },
};

/** EP comping cells per bar: attacks [step, lengthInSteps]. 'push' anticipates the next chord on step 14. */
export const COMPS = {
  hold: [[0, 16]],
  charleston: [[0, 3], [6, 10]],
  pulse: [[0, 6], [8, 8]],
};

/** Swing on 16ths: odd steps are delayed by (pct − 50) / 50 × stepDur. */
export const swing = (step, stepDur, pct) => (step % 2 ? ((pct - 50) / 50) * stepDur : 0);

export const HAT_ACCENT = [1, 0.45, 0.7, 0.45];
const VEL = { kick: 0.92, snare: 0.88, rim: 0.8, hat: 0.8, clap: 0.85, shaker: 0.7, tom: 0.85, block: 0.75 };

/**
 * Drum hits for one bar.
 * b: { sec, j (bar in section), n (section length), drums ('none'|'hats2'|'pattern'|'P5'|'outro'|'pickup'), hatsFirst, extra }
 *    'pickup' is a cold start's one bar (§7): a kick under the chord, then the style's roll (else F1) into the first A.
 * style: the track's style (site/js/audio/styles): its parsed grids `drums` (the break's groove is `drums.break`, else P5),
 *        `fills` and `bVariation` (false: no open hat or ghost snares in B). Without one, the grids and fills above.
 * → { hits: [{ v, s, vel, open, ghost }], stop: step | null, fill: id | null }
 */
export function barHits(plan, b, r, style = null) {
  const out = { hits: [], stop: null, fill: null };
  const mode = b.drums;
  if (mode === 'none' || !mode) return out;
  if (mode === 'pickup') {
    const f = Object.values(style?.fills || FILLS).find(x => x.roll) || FILLS.F1, rv = f.voice || 'snare', lv = plan.drums.level;
    out.hits = [{ v: 'kick', s: 0, vel: VEL.kick * lv }, ...f.roll.map(([s, v]) => ({ v: rv, s, vel: v * lv }))];
    return out;
  }
  if (mode === 'hats2' && b.j < b.n - 2) return out;
  if (mode === 'outro' && b.j >= 2) return out;
  const G = style?.drums || DRUMS, fills = style?.fills || FILLS;
  const grid = mode === 'P5' ? G.break || DRUMS.P5 : G[plan.drums.pattern] || DRUMS[plan.drums.pattern];
  const scale = (mode === 'P5' ? 0.6 : 1) * plan.drums.level;
  let hits = grid.hits.map(h => ({ ...h }));
  if (mode === 'hats2' || (b.hatsFirst && b.j === 0)) hits = hits.filter(h => h.v === 'hat' || h.v === 'shaker');
  if (mode === 'outro') hits = hits.filter(h => h.v !== 'kick');
  const groove = mode === 'pattern' || mode === 'P5';
  if (groove && b.j > 0 && r() < 0.08) hits = hits.filter(h => h.v !== 'kick');       // a bar without kick
  if (mode === 'pattern' && b.sec === 'B' && style?.bVariation !== false) {           // B: open hat + ghosts
    if (b.j % 2 === 1) {
      const h14 = hits.find(h => h.v === 'hat' && h.s === 14);
      if (h14) h14.open = true; else hits.push({ v: 'hat', s: 14, open: true });
      hits = hits.filter(h => !(h.v === 'hat' && h.s === 15));
    }
    for (const s of [7, 15]) if (r() < 0.3 && !hits.some(h => h.v === 'snare' && h.s === s)) hits.push({ v: 'snare', s, ghost: true });
  }
  // fills in the last bar of an 8-bar (p .5) or 4-bar (p .2) phrase
  if (groove && b.j < b.n) {
    const pos = b.j + 1, p = pos % 8 === 0 ? 0.5 : pos % 4 === 0 ? 0.2 : 0;
    if (p && r() < p) {
      const ids = Object.keys(fills), id = ids[Math.floor(r() * ids.length)], f = fills[id];
      out.fill = id;
      if (f.roll) {
        const rv = f.voice || 'snare';
        hits = hits.filter(h => !(h.v === rv && h.s >= 12));
        for (const [s, v] of f.roll) hits.push({ v: rv, s, vel: v });
      }
      if (f.stop != null) { hits = hits.filter(h => h.s < f.stop); out.stop = f.stop; }
      if (f.kicks) { const kv = f.voice || 'kick'; for (const s of f.kicks) if (!hits.some(h => h.v === kv && h.s === s)) hits.push({ v: kv, s, vel: 0.7 }); }
      if (f.open != null) {
        hits = hits.filter(h => !(h.v === 'hat' && h.s >= f.open));
        hits.push({ v: 'hat', s: f.open, open: true });
      }
    }
  }
  for (const h of hits) {
    let v = h.vel ?? VEL[h.v] ?? 0.8;
    if (h.v === 'hat') v *= h.open ? 0.95 : HAT_ACCENT[h.s % 4];
    if (h.v === 'kick' && h.s !== 0 && h.vel == null) v *= 0.9;
    if (h.acc) v *= h.acc;
    if (h.ghost) v = 0.25 + 0.1 * r();
    h.vel = v * scale;
  }
  out.hits = hits.sort((a, b2) => a.s - b2.s);
  return out;
}
